import { useEffect, useContext, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import ProductSidebar from '../components/ProductSidebar';
import Pagination from '../components/Pagination';
import WishlistButton from '../components/WishlistButton';
import FilterTags from '../components/FilterTags';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import { formatPrice, getPrimaryImage, isInStock, getStockDisplay, handleImageError, addCacheBuster } from '../utils/apiHelpers';
import useContentUpdates from '../hooks/useContentUpdates';

// Hardcoded content - preserved for fallback and feature flag toggle
const HARDCODED_SECTION_HEADER = {
  title: '2026 FIFA World Cup',
  description: 'The FIFA World Cup is a once in 4 years tournament that attracts close to 2 Billion viewers from around the world'
};

// Fallback background image (used when API is disabled or fails)
const FALLBACK_PRODUCT_LISTING_BACKGROUND = '/assets/img/banner1.1.jpg';

function ProductListing() {
  const { addToCart, loading: cartLoading } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Section header state (CMS or hardcoded)
  const [sectionHeader, setSectionHeader] = useState(HARDCODED_SECTION_HEADER);

  // Background image state (CMS or hardcoded)
  const [backgroundUrl, setBackgroundUrl] = useState(FALLBACK_PRODUCT_LISTING_BACKGROUND);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter state variables
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [priceRange, setPriceRange] = useState([0, 800]);
  const [sortBy, setSortBy] = useState('default');
  const [filterLoading, setFilterLoading] = useState(false);
  const [sliderInstance, setSliderInstance] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [pageSize] = useState(12); // Items per page
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Per-product image cache-busting keys for WebSocket updates
  const [productImageKeys, setProductImageKeys] = useState(new Map());

  // Fetch section header from CMS - extracted to reusable function for WebSocket updates
  const fetchSectionHeader = useCallback(async () => {
    // Check feature flag - skip API call if disabled
    if (!featureFlags.useCmsContent()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProductListing] CMS content disabled, using hardcoded content');
      }
      return;
    }

    try {
      const result = await contentService.getSectionHeader('2026-fifa-world-cup');
      if (result.success && result.data) {
        setSectionHeader({
          title: result.data.title,
          description: result.data.description
        });
        if (process.env.NODE_ENV === 'development') {
          console.log('[ProductListing] Loaded section header from CMS');
        }
      } else {
        console.warn('[ProductListing] CMS fetch failed, using hardcoded content');
      }
    } catch (err) {
      console.error('[ProductListing] Error fetching section header:', err);
      // Fallback to hardcoded content (already set as initial state)
    }
  }, []);

  // Fetch page background from CMS - extracted to reusable function for WebSocket updates
  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[ProductListing] Dynamic backgrounds disabled, using fallback');
      }
      return;
    }

    try {
      const result = await contentService.getPageBackground('product-listing');

      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
        if (process.env.NODE_ENV === 'development') {
          console.log('[ProductListing] Loaded background from API:', result.imageUrl);
        }
      } else {
        console.warn('[ProductListing] Failed to load background, using fallback');
        setBackgroundUrl(FALLBACK_PRODUCT_LISTING_BACKGROUND);
      }
    } catch (err) {
      console.error('[ProductListing] Error fetching background:', err);
      setBackgroundUrl(FALLBACK_PRODUCT_LISTING_BACKGROUND);
    }
  }, []);

  // Fetch products from backend - extracted to reusable function for WebSocket updates
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);

      // Build parameters
      const params = {
        page: currentPage,
        size: pageSize
      };

      // Add search parameter if exists
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add sort parameter if not default
      if (sortBy && sortBy !== 'default') {
        params.sortBy = sortBy;
      }

      // Use size parameter as per backend API (0-indexed pagination)
      const response = await productService.getProducts(params);
      if (response.success) {
        setProducts(response.products || []);
        // Only update pagination if response contains it
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.error || 'Failed to load products');
      }
    } catch (err) {
      console.error('[ProductListing] Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy]); // Added sortBy to dependencies

  // Fetch categories - extracted to reusable function for WebSocket updates
  const fetchCategories = useCallback(async () => {
    try {
      const response = await productService.getCategories();
      if (response.success) {
        setCategories(response.categories || []);
      }
    } catch (err) {
      console.error('[ProductListing] Error fetching categories:', err);
    }
  }, []);

  // Read search param from URL on mount
  useEffect(() => {
    const query = searchParams.get('search');
    if (query) {
      setSearchQuery(query);
    }
  }, [searchParams]);

  // Initial fetch on mount
  useEffect(() => {
    fetchSectionHeader();
  }, [fetchSectionHeader]);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Listen for real-time content updates via WebSocket
  useContentUpdates({
    onPageUpdated: (slug, title) => {
      // Refresh section header when '2026-fifa-world-cup' slug is updated
      if (slug === '2026-fifa-world-cup') {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductListing] Real-time update received for section header - ${title}`);
        }
        fetchSectionHeader();
        toast.info('Product listing header has been updated', { autoClose: 2000 });
      }
    },

    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[ProductListing] Background updated via WebSocket, refetching');
        }
        fetchBackground();
      }
    },

    onProductUpdated: (productId, sku, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductListing] Product ${productId} updated: ${name} (${sku}) - refreshing image for this product only`);
      }

      // Only refresh cache for this specific product
      setProductImageKeys(prev => {
        const newMap = new Map(prev);
        newMap.set(productId, Date.now());
        return newMap;
      });

      // Refetch only this specific product to get updated data
      productService.getProduct(productId).then(response => {
        if (response.success && response.product) {
          setProducts(prev =>
            prev.map(p => p.id === productId ? response.product : p)
          );
        }
      }).catch(err => {
        console.error('[ProductListing] Error refetching product after update:', err);
        // Fallback: refetch all products if single product fetch fails
        fetchProducts();
      });
    },

    onProductDeleted: (productId) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductListing] Product deleted: ${productId}`);
      }
      // Remove product from list immediately
      setProducts(prev => prev.filter(p => p.id !== productId));
      toast.warning('A product has been removed', { autoClose: 2000 });
    },

    onCategoryUpdated: (categoryId, slug, name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductListing] Category updated: ${name}`);
      }
      // Refetch categories to show updated information
      fetchCategories();
    },

    onCategoryDeleted: (categoryId) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ProductListing] Category deleted: ${categoryId}`);
      }
      // Remove category from list and clear filter if it was selected
      setCategories(prev => prev.filter(c => c.id !== categoryId));
      if (selectedCategory === categoryId) {
        setSelectedCategory(null);
        fetchProducts(); // Refresh products without filter
      }
      toast.warning('A category has been removed', { autoClose: 2000 });
    }
  });

  // Handler to add product to cart
  const handleAddToCart = async (e, productId, productName) => {
    e.preventDefault();
    e.stopPropagation();

    const result = await addToCart(productId, 1);
    if (result.success) {
      toast.success(`${productName} added to cart!`);
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };


  useEffect(() => {
    // Initialize all plugins on window load
    const initPlugins = () => {
      if (window.$ && window.$.fn) {
        try {
          // Initialize Owl Carousel for Product Listings
          if (window.$.fn.owlCarousel) {
            const productCarousels = window.$('.product-listing-carousel');
            if (productCarousels.length > 0) {
              productCarousels.each(function() {
                const carousel = window.$(this);
                if (!carousel.data('owl.carousel')) {
                  carousel.owlCarousel({
                    loop: true,
                    margin: 0,
                    dots: false,
                    nav: true,
                    responsive: {
                      0: { items: 1 },
                      600: { items: 1 },
                      1000: { items: 1 }
                    }
                  });
                }
              });
            }
          }

          // Parallax banner is now handled by ParallaxBanner component

          // Initialize Sticky sidebar
          if (window.Stickyfill) {
            const stickyElements = window.$('.sticky');
            if (stickyElements.length > 0) {
              window.Stickyfill.add(stickyElements);
            }
          }

          // Initialize noUiSlider for price range
          if (window.noUiSlider) {
            const rangeSlider = document.getElementById('slider-range');
            if (rangeSlider && !rangeSlider.noUiSlider) {
              window.noUiSlider.create(rangeSlider, {
                start: [0, 800],
                connect: true,
                range: {
                  'min': 0,
                  'max': 800
                }
              });

              // Store slider instance for cleanup
              setSliderInstance(rangeSlider);

              const minPrice = document.getElementById('min-p');
              const maxPrice = document.getElementById('max-p');

              // Update display values while sliding
              rangeSlider.noUiSlider.on('update', function (values, handle) {
                const value = values[handle];
                if (handle === 0) {
                  minPrice.textContent = '£' + Math.round(value);
                } else {
                  maxPrice.textContent = ' - £' + Math.round(value);
                }
              });

              // Update state when user releases slider (for filtering)
              rangeSlider.noUiSlider.on('change', function (values) {
                setPriceRange([Math.round(values[0]), Math.round(values[1])]);
              });
            }
          }
        } catch (error) {
          console.error('Plugin initialization error:', error);
        }
      }
    };

    // Handle window load event
    const handleWindowLoad = () => {
      // Fade out loader
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
      }

      // Initialize plugins after loader starts fading
      initPlugins();
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      // Otherwise wait for window load event
      window.addEventListener('load', handleWindowLoad);
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
      // Cleanup slider on unmount
      if (sliderInstance && sliderInstance.noUiSlider) {
        sliderInstance.noUiSlider.destroy();
      }
    };
  }, [sliderInstance]);

  // Handler to apply filters
  const handleApplyFilter = async () => {
    try {
      setFilterLoading(true);
      setError(null);

      // Reset to first page when applying filters
      setCurrentPage(0);

      // Build filter parameters
      const params = {
        page: 0, // Always start from first page when filtering
        size: pageSize
      };

      // Add search parameter if exists
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add sort parameter if not default
      if (sortBy && sortBy !== 'default') {
        params.sortBy = sortBy;
      }

      // Add category filter if selected
      if (selectedCategory) {
        params.category = selectedCategory;
      }

      // Add price range filter (backend expects values in cents, so multiply by 100)
      if (priceRange[0] > 0 || priceRange[1] < 800) {
        params.minPrice = priceRange[0] * 100;
        params.maxPrice = priceRange[1] * 100;
      }

      // Fetch filtered products
      const response = await productService.getProducts(params);

      if (response.success) {
        setProducts(response.products || []);
        setPagination(response.pagination || pagination);
      } else {
        setError(response.error || 'Failed to filter products');
      }
    } catch (err) {
      console.error('[ProductListing] Error applying filters:', err);
      setError('Failed to apply filters');
    } finally {
      setFilterLoading(false);
    }
  };

  // Handler to clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchParams({}); // Clear URL params
    setCurrentPage(0);
    // fetchProducts will be triggered by useEffect when searchQuery changes
  };

  // Handler to clear filters
  const handleClearFilters = useCallback(() => {
    setSelectedCategory(null);
    setPriceRange([0, 800]);
    setSortBy('default');
    if (sliderInstance && sliderInstance.noUiSlider) {
      sliderInstance.noUiSlider.set([0, 800]);
    }
    setCurrentPage(0); // Reset to first page when clearing filters
  }, [sliderInstance]);

  // Handler for sort changes
  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(0); // Reset to first page when sorting changes
  }, []);

  // Individual filter removal handlers
  const handleRemoveSearch = useCallback(() => {
    setSearchQuery('');
    setSearchParams({});
    setCurrentPage(0);
  }, [setSearchParams]);

  const handleRemovePrice = useCallback(() => {
    setPriceRange([0, 800]);
    if (sliderInstance && sliderInstance.noUiSlider) {
      sliderInstance.noUiSlider.set([0, 800]);
    }
    setCurrentPage(0);
  }, [sliderInstance]);

  const handleRemoveSort = useCallback(() => {
    setSortBy('default');
    setCurrentPage(0);
  }, []);

  const handleRemoveCategory = useCallback(() => {
    setSelectedCategory(null);
    setCurrentPage(0);
  }, []);

  // Clear all filters including search
  const handleClearAllFilters = useCallback(() => {
    handleClearFilters();
    if (searchQuery) {
      handleRemoveSearch();
    }
  }, [searchQuery, handleRemoveSearch, handleClearFilters]);

  return (
    <Layout>
      {/* Parallax Banner - Reusable Component */}
      <ParallaxBanner
        imageUrl={backgroundUrl}
        speed={0.6}
        offset={-100}
        className="paralax-section-slide-data1"
      />

      {/* Product Listing Start */}
      <div className="product-listing">
        <div className="container">
          <div className="row no-gutters">

            {/* Sidebar with filters */}
            <ProductSidebar
              categories={categories}
              currentCategorySlug={null}
              priceRange={priceRange}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              onClearFilters={handleClearFilters}
              onApplyFilter={handleApplyFilter}
              filterLoading={filterLoading}
              sliderInstance={sliderInstance}
            />

            {/* START PRODUCT COL 8 */}
            <div className="col-md-12 col-lg-8 order-1 order-lg-2">
              <div className="row">

                {/* START LISTING HEADING */}
                <div className="col-12 product-listing-heading">
                  <h1 className="heading text-left">{sectionHeader.title}</h1>
                  <div
                    className="para_text text-left"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sectionHeader.description) }}
                  />
                </div>
                {/* END LISTING HEADING */}

                {/* FILTER TAGS */}
                <div className="col-12">
                  <FilterTags
                    searchQuery={searchQuery}
                    priceRange={priceRange}
                    sortBy={sortBy}
                    selectedCategory={selectedCategory}
                    categories={categories}
                    onRemoveSearch={handleRemoveSearch}
                    onRemovePrice={handleRemovePrice}
                    onRemoveSort={handleRemoveSort}
                    onRemoveCategory={handleRemoveCategory}
                    onClearAll={handleClearAllFilters}
                  />
                </div>

                {/* SEARCH INDICATOR */}
                {searchQuery && (
                  <div className="col-12 mb-4">
                    <div className="search-indicator">
                      <div className="search-indicator-content">
                        <i className="fas fa-search me-2"></i>
                        <span>
                          Searching for: <strong>"{searchQuery}"</strong>
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary ms-3"
                          onClick={handleClearSearch}
                        >
                          <i className="fas fa-times me-1"></i>
                          Clear Search
                        </button>
                      </div>
                      {!loading && (
                        <p className="search-results-count mb-0">
                          Found {products.length} {products.length === 1 ? 'product' : 'products'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* START PRODUCT LISTING SECTION */}
                <div className="col-12 product-listing-products">

                  {/* Loading State */}
                  {loading && (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading products...</p>
                    </div>
                  )}

                  {/* Error State */}
                  {error && !loading && (
                    <div className="alert alert-warning text-center" role="alert">
                      {error}. Please try again later.
                    </div>
                  )}

                  {/* Empty State */}
                  {!loading && !error && products.length === 0 && (
                    <div className="empty-search-results text-center py-5">
                      {searchQuery ? (
                        <>
                          <div className="empty-icon mb-4">
                            <i className="fas fa-search" style={{ fontSize: '60px', color: '#ddd' }}></i>
                          </div>
                          <h3 className="mb-3">No products found for "{searchQuery}"</h3>
                          <p className="text-muted mb-4">
                            Try adjusting your search or browse our categories
                          </p>
                          <button
                            className="btn pink-gradient-btn-into-black"
                            onClick={handleClearSearch}
                          >
                            Clear Search
                          </button>
                        </>
                      ) : (
                        <p>No products available at the moment.</p>
                      )}
                    </div>
                  )}

                  {/* Products Grid - Dynamic from API */}
                  {!loading && !error && products.length > 0 && (
                    <div className="product-list row">
                      {products.map((product) => {
                        // Only cache-bust if product was updated via WebSocket
                        const cacheKey = productImageKeys.get(product.id);

                        return (
                          <div
                            key={product.id}
                            className="col-12 col-md-6 col-lg-4 manage-color-hover wow slideInUp"
                            data-wow-delay=".2s"
                          >
                            <div className="product-item">
                              <div className="item p-item-img">
                                <Link to={`/product-detail/${product.id}`}>
                                  <img
                                    src={cacheKey ? addCacheBuster(getPrimaryImage(product), cacheKey) : getPrimaryImage(product)}
                                    alt={product.name}
                                    onError={handleImageError}
                                    loading="lazy"
                                  />
                              </Link>
                              <div className="text-center d-flex justify-content-center align-items-center">
                                <button
                                  className="listing-cart-icon"
                                  onClick={(e) => handleAddToCart(e, product.id, product.name)}
                                  disabled={cartLoading || !isInStock(product)}
                                  aria-label={`Add ${product.name} to cart`}
                                >
                                  <i className="fa fa-shopping-cart"></i>
                                </button>
                              </div>
                              {/* Wishlist button - top left */}
                              <div
                                className="position-absolute"
                                style={{top: '10px', left: '10px', zIndex: 10}}
                              >
                                <WishlistButton
                                  productId={product.id}
                                  className="product-card-wishlist-btn"
                                />
                              </div>
                              {/* Stock badge */}
                              {(() => {
                                const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                                // Only show badge for out of stock or low stock
                                if (stockInfo.className === 'in-stock') return null;
                                return (
                                  <span
                                    className={`badge position-absolute stock-badge ${stockInfo.className}`}
                                    style={{top: '10px', right: '10px'}}
                                  >
                                    {stockInfo.className === 'out-of-stock' ? 'Out of Stock' : `Only ${product.stock} left!`}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          <div className="p-item-detail">
                            <h4 className="text-center p-item-name">
                              <Link to={`/product-detail/${product.id}`}>
                                {product.name}
                              </Link>
                            </h4>
                            <p className="text-center p-item-price">
                              {formatPrice(product.price)}
                              {product.compareAtPrice && (
                                <span
                                  className="ms-2"
                                  style={{
                                    fontSize: '0.85em',
                                    textDecoration: 'line-through',
                                    color: '#999',
                                    opacity: 0.7
                                  }}
                                  title="Original price"
                                >
                                  {formatPrice(product.compareAtPrice)}
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Pagination */}
                  {!loading && !error && products.length > 0 && (
                    <Pagination
                      pagination={pagination}
                      currentPage={currentPage}
                      onPageChange={setCurrentPage}
                    />
                  )}

                </div>
                {/* END PRODUCT LISTING SECTION */}
              </div>
            </div>
            {/* END PRODUCT COL 8 */}

          </div>
        </div>
      </div>
      {/* Product Listing End */}
    </Layout>
  );
}

export default ProductListing;
